import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Image, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { CustomForm, CustomModal } from "../../components";
import {
  useCreateTransferNewsMutation,
  useDeleteTransferNewsMutation,
  useFetchAllTransferNewsQuery,
  useFilterTransferNewsByUserQuery,
  useUpdateTransferNewsMutation,
} from "../../store/api/apiSlice";

const TransferNewsManagement = () => {
  const { data: transferNewsData = { news: [] }, refetch } =
    useFetchAllTransferNewsQuery();
  const [createTransferNews] = useCreateTransferNewsMutation();
  const [deleteTransferNews] = useDeleteTransferNewsMutation();
  const [updateTransferNews] = useUpdateTransferNewsMutation();
  const genderOptions = ["male", "female", "other"];
  const [isModalOpen, setModalOpen] = useState(false);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [currentNewsId, setCurrentNewsId] = useState(null);
  const [currentViewNews, setCurrentViewNews] = useState(null);
  const [filterParams, setFilterParams] = useState(null);
  const [image, setImage] = useState(null);
  const [schools, setSchools] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [tags, setTags] = useState([]);
  const { data: filteredTransferNewsData } = useFilterTransferNewsByUserQuery(
    filterParams,
    { skip: !filterParams }
  );

  const titleRef = useRef(null);
  const categoryRef = useRef(null);
  const sourceRef = useRef(null);
  const tagsRef = useRef(null);
  const sportsTagsRef = useRef(null);
  const genderRef = useRef(null);
  const schoolNameRef = useRef(null);
  const schoolMascotRef = useRef(null);
  const detailsRef = useRef(null);
  const urlRef = useRef(null);
  const expiryDateRef = useRef(null);
  const baseUrl = process.env.REACT_APP_SERVER_URL;
  const secretKey = process.env.REACT_APP_SECRET_KEY;

  useEffect(() => {
    const fetchSchools = async () => {
      const xAuthToken =
        JSON.parse(localStorage.getItem("token") || "{}")?.token || "";
      const headers = {
        "secret-key": secretKey,
        "x-auth-token": xAuthToken,
      };

      try {
        const response = await fetch(`${baseUrl}/api/university/get-all`, {
          method: "GET",
          headers,
        });
        if (response.ok) {
          const data = await response.json();
          setSchools(data.university || []);
        } else {
          toast.error("Failed to fetch schools");
        }
      } catch (error) {
        toast.error("An error occurred while fetching schools");
      }
    };

    fetchSchools();
  }, []);
  const openModal = (news = null) => {
    setSelectedNews(news);
    setModalOpen(true);
    setIsEditing(news);
    setTags(news.tags || []);
    setImage(news.image);
    setCurrentNewsId(news._id);
  };
  const handleUpdate = (news) => {
    openModal(news);
  };
  const closeModal = () => {
    setModalOpen(false);
    setIsEditing(false);
    setImage(null);
  };

  const openViewModal = async (id) => {
    try {
      const response = await fetch(
        `${baseUrl}/api/transfer-news/get-single/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "secret-key": process.env.REACT_APP_SECRET_KEY,
            "x-auth-token":
              JSON.parse(localStorage.getItem("token"))?.token || "",
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCurrentViewNews(data.news);
        setViewModalOpen(true);
      } else {
        toast.error("Failed to fetch transfer news details.");
      }
    } catch (error) {
      toast.error("An error occurred while fetching news details.");
    }
  };

  const closeViewModal = () => setViewModalOpen(false);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const transferNewsData = {
      title: titleRef.current?.value || "",
      category: categoryRef.current?.value || "",
      source: sourceRef.current?.value || "",
      tags: tags.map((tag) => tag.trim()),
      sports_tags: sportsTagsRef.current?.value.split(","),
      gender: genderRef.current?.value || "",
      schoolName: schoolNameRef.current?.value || "",
      schoolMascot: schoolMascotRef.current?.value || "",
      details: detailsRef.current?.value || "",
      url: urlRef.current?.value || "",
      expiry_date: expiryDateRef.current?.value || "",
      image,
    };

    try {
      if (isEditing) {
        const response = await updateTransferNews({
          id: currentNewsId,
          newsData: transferNewsData,
        }).unwrap();

        toast.success("Transfer news updated successfully!");
      } else {
        await createTransferNews(transferNewsData).unwrap();
        toast.success("Transfer news created successfully!");
      }
      refetch();
      closeModal();
      setTags([]);
    } catch (error) {
      toast.error(
        isEditing
          ? "Error updating transfer news"
          : "Error creating transfer news"
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTransferNews(id).unwrap();
      toast.success("Transfer news deleted successfully");
      refetch();
    } catch (error) {
      toast.error("Error deleting transfer news");
    }
  };

  const handleEdit = (news) => {
    if (titleRef.current) titleRef.current.value = news.title || "";
    if (categoryRef.current) categoryRef.current.value = news.category || "";
    if (sourceRef.current) sourceRef.current.value = news.source || "";
    if (tagsRef.current) tagsRef.current.value = news.tags.join(", ");
    if (sportsTagsRef.current)
      sportsTagsRef.current.value = news.sports_tags.join(", ");
    if (genderRef.current) genderRef.current.value = news.gender || "";
    if (schoolNameRef.current)
      schoolNameRef.current.value = news.schoolName || "";
    if (schoolMascotRef.current)
      schoolMascotRef.current.value = news.schoolMascot || "";
    if (detailsRef.current) detailsRef.current.value = news.details || "";
    if (urlRef.current) urlRef.current.value = news.url || "";
    if (expiryDateRef.current)
      expiryDateRef.current.value = new Date(news.expiry_date)
        .toISOString()
        .split("T")[0];
    setImage(`${baseUrl}/${news.image}`);
    setCurrentNewsId(news._id);
    setIsEditing(true);
    openModal();
  };

  const handleFilter = (e) => {
    e.preventDefault();
    const filterData = {
      title: titleRef.current?.value || "",
      category: categoryRef.current?.value || "",
      gender: genderRef.current?.value || "",
      schoolName: schoolNameRef.current?.value || "",
    };
    setFilterParams(filterData);
  };

  const displayedTransferNews = filterParams
    ? filteredTransferNewsData?.news || []
    : transferNewsData.news;
  const paginatedUsers = displayedTransferNews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(displayedTransferNews.length / itemsPerPage);
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue.trim());
      setInputValue("");
    }
  };

  const addTag = (tag) => {
    if (!tags.includes(tag)) {
      setTags((prevTags) => [...prevTags, tag]);
    }
  };

  const removeTag = (tagToRemove) => {
    setTags((prevTags) => prevTags.filter((tag) => tag !== tagToRemove));
  };
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "22px",
        }}
      >
        <h3>Transfer News Management</h3>
        <Button onClick={(news) => openModal(!news)}>
          Create Transfer News
        </Button>
      </div>
      <Form onSubmit={handleFilter} className="mt-4 mb-4">
        <Row>
          <Col>
            <Form.Control placeholder="Title" ref={titleRef} />
          </Col>
          <Col>
            <Form.Control placeholder="Category" ref={categoryRef} />
          </Col>
          <Col>
            <Form.Control placeholder="Gender" ref={genderRef} />
          </Col>
          <Col>
            <Form.Control placeholder="School Name" ref={schoolNameRef} />
          </Col>
          <Col>
            <Button type="submit">Apply Filter</Button>
          </Col>
        </Row>
      </Form>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Title</th>
            <th>Category</th>
            <th>Source</th>
            <th>Tags</th>
            <th>Sports Tags</th>
            <th>Gender</th>
            <th>School Name</th>
            <th>School Mascot</th>

            <th>URL</th>
            <th>Expiry Date</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedUsers && paginatedUsers.length > 0 ? (
            paginatedUsers.map((news) => (
              <tr
                key={news._id}
                onClick={() => openViewModal(news._id)}
                style={{ cursor: "pointer" }}
              >
                <td>{news.title}</td>
                <td>{news.category}</td>
                <td>{news.source}</td>
                <td>{news.tags.join(", ")}</td>
                <td>{news.sports_tags.join(", ")}</td>
                <td>{news.gender}</td>
                <td>{news.schoolName}</td>
                <td>{news.schoolMascot}</td>

                <td>
                  <a href={news.url} target="_blank" rel="noopener noreferrer">
                    Link
                  </a>
                </td>
                <td>{new Date(news.expiry_date).toLocaleDateString()}</td>
                <td>
                  {news.image && (
                    <Image
                      src={news.image}
                      alt="news"
                      width="50"
                      height="50"
                      thumbnail
                    />
                  )}
                </td>
                <td>
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      className="Update-delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdate(news);
                      }}
                      width="30"
                      height="30"
                      src="https://img.icons8.com/ios-glyphs/30/create-new.png"
                      alt="create-new"
                    />

                    <img
                      className="Update-delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(news._id);
                      }}
                      width="30"
                      height="30"
                      src="https://img.icons8.com/ios-glyphs/30/filled-trash.png"
                      alt="filled-trash"
                    />
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="13" className="text-center">
                No transfer news available.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <div className="d-flex justify-content-center mt-3">
        {Array.from({ length: totalPages }, (_, index) => (
          <Button
            key={index}
            variant={currentPage === index + 1 ? "primary" : "outline-primary"}
            onClick={() => setCurrentPage(index + 1)}
            className="mx-1"
          >
            {index + 1}
          </Button>
        ))}
      </div>

      <CustomModal
        isOpen={isModalOpen}
        onClose={closeModal}
        className="scrollable-modal"
      >
        <div style={{ padding: "1rem", maxHeight: "80vh", overflowY: "auto" }}>
          <h4
            style={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            {isEditing ? "Edit Transfer News" : "Create Transfer News"}
          </h4>
          <CustomForm onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control
                ref={titleRef}
                defaultValue={selectedNews?.title}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Form.Control
                ref={categoryRef}
                defaultValue={selectedNews?.category}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Source</Form.Label>
              <Form.Control
                ref={sourceRef}
                defaultValue={selectedNews?.source}
                required
              />
            </Form.Group>
            <Form.Group className="pb-4" controlId="tags">
              <Form.Label>Tags</Form.Label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              >
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "2px",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "3px",
                    }}
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      style={{
                        marginLeft: "5px",
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: "#ce1126",
                      }}
                    >
                      x
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder="Enter tags"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  style={{
                    border: "none",
                    outline: "none",
                    flex: "1",
                    minWidth: "100px",
                    padding: "6px 18px",
                  }}
                />
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Details</Form.Label>
              <Form.Control
                as="textarea"
                ref={detailsRef}
                defaultValue={selectedNews?.details}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>URL</Form.Label>
              <Form.Control
                ref={urlRef}
                defaultValue={selectedNews?.url}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>School Name</Form.Label>
              <Form.Select
                ref={schoolNameRef}
                defaultValue={selectedNews?.schoolName}
                required
              >
                <option value="">Select School</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.name}>
                    {school.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>School Mascot</Form.Label>
              <Form.Control
                ref={schoolMascotRef}
                defaultValue={selectedNews?.schoolMascot}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Gender</Form.Label>
              <Form.Select
                ref={genderRef}
                defaultValue={selectedNews?.gender}
                required
              >
                <option value="">Select Gender</option>
                {genderOptions.map((gender) => (
                  <option key={gender} value={gender}>
                    {gender}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Image</Form.Label>
              <Form.Control type="file" onChange={handleImageChange} />
              {image && (
                <Image src={image} alt="Preview" width="100" className="mt-2" />
              )}
            </Form.Group>
            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={closeModal} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {isEditing ? "Update" : "Save"}
              </Button>
            </div>
          </CustomForm>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={isViewModalOpen}
        onClose={closeViewModal}
        className="scrollable-modal"
      >
        <div style={{ padding: "1rem", maxHeight: "80vh", overflowY: "auto" }}>
          <h4
            style={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            Transfer News Details
          </h4>
          {currentViewNews && (
            <div>
              <p>
                <strong>Title:</strong> {currentViewNews.title}
              </p>
              <p>
                <strong>Category:</strong> {currentViewNews.category}
              </p>
              <p>
                <strong>Source:</strong> {currentViewNews.source}
              </p>
              <p>
                <strong>Tags:</strong> {currentViewNews.tags.join(", ")}
              </p>
              <p>
                <strong>Sports Tags:</strong>{" "}
                {currentViewNews.sports_tags.join(", ")}
              </p>
              <p>
                <strong>Gender:</strong> {currentViewNews.gender}
              </p>
              <p>
                <strong>School Name:</strong> {currentViewNews.schoolName}
              </p>
              <p>
                <strong>School Mascot:</strong> {currentViewNews.schoolMascot}
              </p>
              <p>
                <strong>Details:</strong> {currentViewNews.details}
              </p>
              <p>
                <strong>URL:</strong>{" "}
                <a
                  href={currentViewNews.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Link
                </a>
              </p>
              <p>
                <strong>Expiry Date:</strong>{" "}
                {new Date(currentViewNews.expiry_date).toLocaleDateString()}
              </p>
              {currentViewNews.image && (
                <Image
                  src={`${baseUrl}/${currentViewNews.image}`}
                  alt="news"
                  width="100"
                  thumbnail
                />
              )}
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default TransferNewsManagement;
